import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { config } from '../config';

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const DEFAULT_ZOOM = 15;

/** Geocode URL: use backend proxy when API is set (avoids CORS from Amplify); otherwise direct Nominatim (works in localhost). */
function geocodeUrl(query: string, limit: number): string {
  const base = (config.apiBaseUrl || '').replace(/\/$/, '');
  if (base) {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    return `${base}/public/geocode?${params}`;
  }
  const params = new URLSearchParams({ q: query, format: 'json', limit: String(limit) });
  return `${NOMINATIM_URL}?${params}`;
}

export interface EmbeddedMapProps {
  /** Search query (e.g. "JLT, Dubai, United Arab Emirates") */
  query: string;
  /** Height of the map container in pixels */
  height?: number;
  /** Optional aria-label for the map container */
  ariaLabel?: string;
}

export default function EmbeddedMap({ query, height = 280, ariaLabel = 'Map' }: EmbeddedMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const [status, setStatus] = useState<'idle' | 'loading' | 'ready' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!query.trim()) {
      setStatus('error');
      setErrorMessage('No location to show');
      return;
    }

    let cancelled = false;
    setStatus('loading');
    setErrorMessage(null);

    const url = geocodeUrl(query, 1);

    fetch(url, {
      headers: {
        Accept: 'application/json',
        ...(url.startsWith('http') && !url.includes('/public/geocode') ? { 'Accept-Language': 'en', 'User-Agent': 'Darlynk-PropertyListings/1.0' } : {}),
      },
    })
      .then((res) => res.json())
      .then((data: { lat: string; lon: string }[]) => {
        if (cancelled || !Array.isArray(data) || data.length === 0) {
          if (!cancelled) {
            setStatus('error');
            setErrorMessage('Location not found');
          }
          return;
        }
        const { lat, lon } = data[0];
        const latitude = parseFloat(lat);
        const longitude = parseFloat(lon);
        if (Number.isNaN(latitude) || Number.isNaN(longitude)) {
          setStatus('error');
          setErrorMessage('Invalid coordinates');
          return;
        }

        if (!containerRef.current) {
          setStatus('error');
          return;
        }

        // Clean previous map instance
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }

        const map = L.map(containerRef.current).setView([latitude, longitude], DEFAULT_ZOOM);
        mapRef.current = map;

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        }).addTo(map);

        const markerIcon = L.divIcon({
          className: 'embedded-map-marker',
          html: '<span style="background:#0d9488;width:24px;height:24px;border-radius:50%;display:block;border:3px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4);"></span>',
          iconSize: [24, 24],
          iconAnchor: [12, 12],
        });
        L.marker([latitude, longitude], { icon: markerIcon }).addTo(map);

        if (!cancelled) setStatus('ready');
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error');
          setErrorMessage('Failed to load map');
        }
      });

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [query]);

  if (!query.trim()) {
    return null;
  }

  return (
    <div style={{ marginBottom: '1.5rem', position: 'relative' }}>
      <div
        ref={containerRef}
        style={{
          height: status === 'ready' ? height : 120,
          width: '100%',
          borderRadius: 12,
          overflow: 'hidden',
          background: '#e5e7eb',
        }}
        aria-label={ariaLabel}
        role="img"
      />
      {status === 'loading' && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: '#f1f5f9',
            borderRadius: 12,
            fontSize: '0.875rem',
            color: '#64748b',
          }}
        >
          Loading map…
        </div>
      )}
      {status === 'error' && errorMessage && (
        <p style={{ marginTop: 8, fontSize: '0.875rem', color: '#b91c1c' }}>{errorMessage}</p>
      )}
    </div>
  );
}
